/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useMemo, useRef } from "react";

import __ from "web/Layout/Translations";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import {
  getNextSelectIndex,
  isSelectCloseKey,
  isSelectCommitKey,
  isSelectNavKey,
} from "web/utils/ui/selectKeyboard";

import { SortOptions, SortOptionsProperty } from "../container";
import classes from "./dropdown.scss";

interface IDropdownProps {
  options: (SortOptions | SortOptionsProperty)[];
  onChange: ({ name, direction }: { name: string; direction?: string }) => void;
  id: string;
  isOpen: boolean;
  activeIndex?: number;
  onRequestActiveIndexChange?: (nextIndex: number) => void;
  onRequestSelect?: (index: number) => void;
  onRequestClose?: () => void;
}

const Dropdown: FC<IDropdownProps> = ({
  options,
  onChange,
  id,
  isOpen,
  activeIndex = -1,
  onRequestActiveIndexChange,
  onRequestSelect,
  onRequestClose,
}) => {
  const classNameOption = classes.option;
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const flatCount = useMemo(() => {
    let count = 0;
    options?.forEach((option) => {
      const opt = option as SortOptionsProperty;
      if (isArrayHasItems(opt.direction)) count += opt.direction.length;
      else count += 1;
    });
    return count;
  }, [options]);

  const safeActiveIndex = useMemo(() => {
    if (activeIndex < 0) return -1;
    if (activeIndex >= flatCount) return flatCount - 1;
    return activeIndex;
  }, [activeIndex, flatCount]);

  useEffect(() => {
    if (!isOpen) return;
    if (safeActiveIndex < 0) return;
    const btn = optionRefs.current.get(`${id}-option-${safeActiveIndex}`);
    btn?.scrollIntoView({ block: "nearest" });
    btn?.focus();
  }, [id, isOpen, safeActiveIndex]);

  let runningIndex = -1;
  return (
    <div className={classes.root} id={id} aria-hidden={!isOpen}>
      {options?.map((option) => {
        return (
          <ul
            className={classes.group}
            key={(option.name || option.label) as string}
            role="listbox"
          >
            {isArrayHasItems((option as SortOptionsProperty).direction)
              ? // @ts-ignore
                option.direction.map((directionItem) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  return (
                    <li key={`${option.name}${directionItem.name}`}>
                      <button
                        key={`${option.name}${directionItem.name}`}
                        className={classNameOption}
                        type="button"
                        data-t2={option.name || option.label}
                        data-t1="dropdown_option_button"
                        tabIndex={isOpen ? 0 : -1}
                        onClick={() => {
                          onChange({
                            name: option.name as string,
                            direction: directionItem.name,
                          });
                        }}
                        onKeyDown={(e) => {
                          if (!isOpen) return;
                          const { key } = e;

                          if (isSelectNavKey(key)) {
                            e.preventDefault();
                            e.stopPropagation();
                            const nextIndex =
                              getNextSelectIndex({
                                key,
                                currentIndex: index,
                                count: flatCount,
                              }) ?? index;
                            onRequestActiveIndexChange?.(nextIndex);
                            return;
                          }

                          if (isSelectCommitKey(key)) {
                            e.preventDefault();
                            e.stopPropagation();
                            onRequestSelect?.(index);
                            return;
                          }

                          if (isSelectCloseKey(key)) {
                            e.preventDefault();
                            e.stopPropagation();
                            onRequestClose?.();
                          }
                        }}
                        id={`${id}-option-${index}`}
                        ref={(node) => {
                          const key = `${id}-option-${index}`;
                          if (!node) {
                            optionRefs.current.delete(key);
                            return;
                          }
                          optionRefs.current.set(key, node);
                        }}
                      >
                        <span
                          className={classes.label}
                          data-t1={`${option.name || option.label}_span`}
                        >
                          {/* @ts-ignore */}
                          {__(option.label)}:
                        </span>{" "}
                        {__(directionItem.label)}
                      </button>
                    </li>
                  );
                })
              : (() => {
                  runningIndex += 1;
                  const index = runningIndex;
                  return (
                    <li key={(option.name || option.label) as string}>
                      <button
                        className={classes.option}
                        type="button"
                        data-t2={option.name || option.label}
                        data-t1="dropdown_option_button"
                        tabIndex={isOpen ? 0 : -1}
                        onClick={() => {
                          onChange({
                            name: option.name as string,
                          });
                        }}
                        onKeyDown={(e) => {
                          if (!isOpen) return;
                          const { key } = e;

                          if (isSelectNavKey(key)) {
                            e.preventDefault();
                            e.stopPropagation();
                            const nextIndex =
                              getNextSelectIndex({
                                key,
                                currentIndex: index,
                                count: flatCount,
                              }) ?? index;
                            onRequestActiveIndexChange?.(nextIndex);
                            return;
                          }

                          if (isSelectCommitKey(key)) {
                            e.preventDefault();
                            e.stopPropagation();
                            onRequestSelect?.(index);
                            return;
                          }

                          if (isSelectCloseKey(key)) {
                            e.preventDefault();
                            e.stopPropagation();
                            onRequestClose?.();
                          }
                        }}
                        id={`${id}-option-${index}`}
                        ref={(node) => {
                          const key = `${id}-option-${index}`;
                          if (!node) {
                            optionRefs.current.delete(key);
                            return;
                          }
                          optionRefs.current.set(key, node);
                        }}
                      >
                        <span
                          className={classes.label}
                          data-t1={`${option.name || option.label}_span`}
                        >
                          {/* @ts-ignore */}
                          {__(option.label)}
                          {option.name && ":"}
                        </span>
                      </button>
                    </li>
                  );
                })()}
          </ul>
        );
      })}
    </div>
  );
};

export default Dropdown;
